// Angular
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
// rxjs
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { ClaimState, fromClaimActions } from '@amfam/claim/data-access';
// Eligible Services
import {
  ClaimServicesState,
  eligibleServicesQuery,
  fromEligibleServicesActions,
  fromRentalServiceActions,
  fromRepairServiceActions,
  rentalServiceQuery,
  repairServiceQuery
} from '@amfam/claim/post-fnol/services-data-access';
import { fromRouterActions, RouterState } from '@amfam/shared/utility/navigation';
// Services

// Misc

// progress indicator
import {
  fromProgressIndicatorActions,
  LoadingSpinnerService,
  progressIndicatorQuery,
  ProgressIndicatorState,
  Step
} from '@amfam/ui-kit';

// Post Fnol
import { fromPostFnolActions } from '../../+state/post-fnol.actions';
import { PostFnolInitPayload } from '../../+state/post-fnol.models';
import { PostFnolState } from '../../+state/post-fnol.reducer';
import { PostFnolRoutingService } from '../../services/post-fnol-routing.service';
import { PostFnolService } from '../../services/post-fnol.service';

@Component({
  selector: 'post-fnol',
  templateUrl: './post-fnol.component.html',
  styleUrls: [],
  standalone: false
})
export class PostFnolComponent implements OnInit, OnDestroy {
  @Input() postFnolData: PostFnolInitPayload;
  @Input() previousUrl: string;

  steps: Observable<Step[]>;
  stepsLoading: Observable<boolean>;

  flowError: Observable<boolean>;

  private stop$ = new Subject<void>();

  constructor(
    private progressIndicatorStore: Store<ProgressIndicatorState>,
    private postFnolStore: Store<PostFnolState>,
    private claimServicesStore: Store<ClaimServicesState>,
    private postFnolService: PostFnolService,
    private postFnolRoutingService: PostFnolRoutingService,
    private spinner: LoadingSpinnerService,
    private routerStore: Store<RouterState>,
    private claimStore: Store<ClaimState>
  ) {}

  ngOnInit() {
    // pass data into store so it can be accessed from child routes
    this.postFnolStore.dispatch(new fromPostFnolActions.InitPostFnolFlow(this.postFnolData));

    // keep up to date steps from progress indicator store
    this.steps = this.progressIndicatorStore.select(
      progressIndicatorQuery.selectProgressIndicatorData
    );
    this.stepsLoading = this.progressIndicatorStore.select(progressIndicatorQuery.selectLoaded);

    // hide the tracker if there is an error with rental or repair
    this.flowError = combineLatest(
      this.claimServicesStore.select(repairServiceQuery.getRepairServiceError),
      this.claimServicesStore.select(rentalServiceQuery.getRentalServiceError),
      (repairError, rentalError) => repairError || rentalError
    );

    // runs once after eligible services have been to determine steps and initializes workflow store
    combineLatest(
      this.claimServicesStore.select(eligibleServicesQuery.getEligibleServicesState),
      this.claimServicesStore.select(eligibleServicesQuery.getClaimServicesStatus),
      (servicesState, servicesStatus) => {
        return {
          servicesState,
          servicesStatus
        };
      }
    )
      .pipe(
        filter(state => state.servicesState.loaded),
        take(1)
      )
      .subscribe(state => {
        // generate steps
        const steps: Step[] = this.postFnolService.getEligibleSteps(
          state.servicesStatus,
          this.previousUrl
        );

        // load steps to progress indicator
        this.progressIndicatorStore.dispatch(
          fromProgressIndicatorActions.ProgressIndicatorLoad({
            workflowType: 'Post Fnol',
            steps: steps,
            activeStep: steps.find(step => step.active).name
          })
        );

        // route to correct first step
        const firstStep = steps.find(step => step.active);
        const path = this.postFnolRoutingService.getStepRoutePath(
          firstStep.name,
          state.servicesState.claimNumber,
          this.postFnolData.context
        );
        this.spinner.stop();
        this.routerStore.dispatch(
          fromRouterActions.Go({
            path: path
          })
        );
      });
  }

  ngOnDestroy() {
    // reset claim services, post-fnol flow, progress indicator, selected claim on leaving post-fnol flow
    this.claimServicesStore.dispatch(new fromEligibleServicesActions.ResetEligibleServices());
    this.claimServicesStore.dispatch(new fromRentalServiceActions.ResetRentalService());
    this.claimServicesStore.dispatch(new fromRepairServiceActions.ResetRepairService());
    this.postFnolStore.dispatch(new fromPostFnolActions.ResetPostFnolFlow());
    this.progressIndicatorStore.dispatch(fromProgressIndicatorActions.ProgressIndicatorDelete());
    this.claimStore.dispatch(fromClaimActions.ClearSelectedClaim());

    // unsubscribe
    this.stop$.next();
    this.stop$.complete();
  }
}
